import * as React from 'react';
import Html, { Rule } from 'slate-html-serializer';
import cssStyles from 'css-styles';

import {
	OBJECT_TYPES,
	BLOCK_TYPES,
	INLINE_TYPES,
	STYLE_TYPES,
	MARK_TYPES,
	MARK_TAGS,
	BLOCK_AND_INLINE_TAGS,
} from './constants';
import { linkClassName } from '../../../../containers/Editor/constants';

/**
 * Slate uses 'nodes' for rendering blocks with text and 'marks' for rendering formatting options (like bold, italic, etc).
 */

// This object defines how to convert input html tags (blocks, hyperlinks) to Slate objects (deserialize) and vice versa (serialize)
const nodeRules: Rule = {
	// tslint:disable-next-line no-any
	deserialize(el: Element, next: (elements: Element[] | NodeList | Array<Node & ChildNode>) => any): any {
		const elData = BLOCK_AND_INLINE_TAGS[el.tagName.toLowerCase()];

		if (elData) {
			const { type, object } = elData;
			return {
				object,
				type,
				data: {
					style: cssStyles.parse(el.getAttribute('style')),
					className: el.getAttribute('class'),
					href: el.getAttribute('href'),
					action: el.getAttribute('onclick'),
				},
				nodes: next(el.childNodes),
			};
		}
	},

	// tslint:disable-next-line no-any
	serialize({ object, type, data, text }: any, children: string): React.ReactNode {
		if (object === OBJECT_TYPES.block) {
			if (type === BLOCK_TYPES.p) {
				return (
					<p
						style={data.get('style')}
						className={data.get('className')}
					>
						{children}
					</p>
				);
			}

			if (type === BLOCK_TYPES.h1) {
				return (
					<h1
						style={data.get('style')}
						className={data.get('className')}
					>
						{children}
					</h1>
				);
			}

			if (type === BLOCK_TYPES.h2) {
				return (
					<h2
						style={data.get('style')}
						className={data.get('className')}
					>
						{children}
					</h2>
				);
			}

			if (type === BLOCK_TYPES.h3) {
				return (
					<h3
						style={data.get('style')}
						className={data.get('className')}
					>
						{children}
					</h3>
				);
			}

			if (type === BLOCK_TYPES.h4) {
				return (
					<h4
						style={data.get('style')}
						className={data.get('className')}
					>
						{children}
					</h4>
				);
			}

			if (type === BLOCK_TYPES.h5) {
				return (
					<h5
						style={data.get('style')}
						className={data.get('className')}
					>
						{children}
					</h5>
				);
			}

			if (type === BLOCK_TYPES.h6) {
				return (
					<h6
						style={data.get('style')}
						className={data.get('className')}
					>
						{children}
					</h6>
				);
			}
		}

		if (object === OBJECT_TYPES.inline) {
			if (type === INLINE_TYPES.a) {
				// We have to remove link when all text for link was deleted
				if (!text) {
					return null;
				}

				return (
					<a
						href={data.get('href')}
						data-action={data.get('action')}
						target="_blank"
						className={`${data.get('className')} ${linkClassName}`}
					>
						{children}
					</a>
				);
			}
		}

		return;
	},
};

// This object defines how to convert input html tags (marks) to Slate objects (deserialize) and vice versa (serialize)
const markRules: Rule = {
	// tslint:disable-next-line no-any
	deserialize(el: Element, next: (elements: Element[] | NodeList | Array<Node & ChildNode>) => any): any {
		if (el.tagName.toLowerCase() === MARK_TAGS.span) {
			const style = el.getAttribute('style');
			const className = el.getAttribute('class');

			if (style) {
				const styleName = style.split(':')[0] as STYLE_TYPES;
				return {
					object: OBJECT_TYPES.mark,
					type: MARK_TYPES.get(styleName) || MARK_TYPES.get(STYLE_TYPES.UNEDITABLE_STYLES),
					data: {
						className,
						style: cssStyles.parse(style),
					},
					nodes: next(el.childNodes),
				};
			}

			if (className) {
				return {
					object: OBJECT_TYPES.mark,
					type: MARK_TYPES.get(STYLE_TYPES.UNEDITABLE_STYLES),
					data: {
						className,
					},
					nodes: next(el.childNodes),
				};
			}
		}
	},

	// tslint:disable-next-line no-any
	serialize({ object, type, data}: any, children: string): React.ReactNode {
		if (object === OBJECT_TYPES.mark) {
			if (MARK_TYPES.has(type)) {
				return (
					<span
						className={data.get('className')}
						style={data.get('style')}
					>
						{children}
					</span>
				);
			}
		}

		return;
	},
};

const rules = [nodeRules, markRules];

export default new Html({ rules });



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/RichTextEditor/helpers/html-serializer.tsx